import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
  fetchVocabularyAsync,
  fetchVocabularyStatsAsync,
  clearError, 
  setFilters, 
  clearFilters,
  setPage
} from '../store/slices/vocabularySlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import LoadingSpinner from './LoadingSpinner';
import { formatDateEuropean } from '../utils/dateUtils';
import './Students.css';
import './GroupForm.css';

const Vocabulary: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const { entries, loading, error, pagination, filters } = useAppSelector((state) => state.vocabulary);
  const { groups } = useAppSelector((state) => state.groups);

  const [searchInput, setSearchInput] = useState(filters.search);
  const [selectedGroupId, setSelectedGroupId] = useState<string>(
    filters.groupId?.toString() || ''
  );
  const [selectedLevel, setSelectedLevel] = useState<string>(filters.level || '');
  const [dateFrom, setDateFrom] = useState<string>(filters.dateFrom || '');
  const [dateTo, setDateTo] = useState<string>(filters.dateTo || '');
  const initialLoadRef = useRef(false);

  const levels = [
    { value: 'a1', label: 'A1' },
    { value: 'a2', label: 'A2' },
    { value: 'b1', label: 'B1' },
    { value: 'b2', label: 'B2' },
    { value: 'c1', label: 'C1' },
    { value: 'c2', label: 'C2' },
  ];

  // Load groups and initial data
  useEffect(() => {
    if (!initialLoadRef.current) {
      dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
      dispatch(fetchVocabularyStatsAsync());
      initialLoadRef.current = true;
    }
  }, [dispatch]);

  // Load vocabulary data when filters change
  const loadVocabulary = useCallback(() => {
    // Load all vocabulary (admin view)
    dispatch(fetchVocabularyAsync({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: filters.search,
      groupId: filters.groupId,
      level: filters.level,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo
    }));
  }, [dispatch, pagination.currentPage, pagination.itemsPerPage, filters]);

  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  const handleSearch = useCallback(() => {
    dispatch(setFilters({ 
      search: searchInput.trim(),
      groupId: selectedGroupId ? parseInt(selectedGroupId) : undefined,
      level: selectedLevel || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined
    }));
  }, [dispatch, searchInput, selectedGroupId, selectedLevel, dateFrom, dateTo]);

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    setSelectedGroupId('');
    setSelectedLevel('');
    setDateFrom('');
    setDateTo('');
    dispatch(clearFilters());
  }, [dispatch]);

  const handlePageChange = useCallback((page: number) => {
    dispatch(setPage(page));
  }, [dispatch]);

  const formatDate = useCallback((dateString: string) => {
    return formatDateEuropean(dateString);
  }, []);

  if (loading && entries.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="students-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Vocabulary</h1>
          <p>Manage vocabulary across all lessons and groups</p>
        </div>
        <button 
          onClick={() => navigate('/lessons')}
          className="btn-primary"
        >
          Manage Lessons
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filter-bar">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by word or lesson topic..."
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
        />

        <select
          value={selectedGroupId}
          onChange={(e) => setSelectedGroupId(e.target.value)}
          className="form-select"
        >
          <option value="">All Groups</option>
          {groups.map(group => (
            <option key={group.id} value={group.id}>
              {group.name || `${group.level.toUpperCase()} Group`}
            </option>
          ))}
        </select>

        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value)}
          className="form-select"
        >
          <option value="">All Levels</option>
          {levels.map(level => (
            <option key={level.value} value={level.value}>
              {level.label}
            </option>
          ))}
        </select>

        <label htmlFor="date-from" className="filter-label">From</label>
        <input
          id="date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="form-input"
        />

        <label htmlFor="date-to" className="filter-label">To</label>
        <input
          id="date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="form-input"
        />

        <button onClick={handleSearch} className="btn-primary">
          Search
        </button>

        <button onClick={handleClearFilters} className="btn-secondary">
          Clear
        </button>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        <p>
          Showing {entries.length} of {pagination.totalItems} vocabulary entries
          {filters.search && ` matching "${filters.search}"`}
          {filters.level && ` at ${filters.level.toUpperCase()} level`}
          {filters.groupId && (
            <>
              {' '}from{' '}
              {groups.find(g => g.id === filters.groupId)?.name || 
               `${groups.find(g => g.id === filters.groupId)?.level.toUpperCase()} Group`}
            </>
          )}
        </p>
      </div>

      {/* Vocabulary Table */}
      <div className="students-table-container vocabulary-table">
        {entries.length === 0 ? (
          <div className="no-students">
            <p>No vocabulary words found.</p>
            {(filters.search || filters.groupId || filters.level) && (
              <button onClick={handleClearFilters} className="btn-primary">
                Clear filters to see all vocabulary
              </button>
            )}
            <button 
              onClick={() => navigate('/lessons')}
              className="btn-secondary"
            >
              Go to Lessons to add vocabulary
            </button>
          </div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>
                <th>Word</th>
                <th>Level</th>
                <th>Group</th>
                <th>Lesson Date</th>
                <th>Lesson Topic</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>
                    {entry.word}
                  </td>
                  <td>
                    {entry.lesson?.group ? entry.lesson.group.level.toUpperCase() : "-"}
                  </td>
                  <td>
                    {entry.lesson?.group ? (
                      entry.lesson.group.name || `Группа ${entry.lesson.group.level.toUpperCase()}`
                    ) : (
                      "Unknown Group"
                    )}
                  </td>
                  <td>
                    {entry.lesson ? formatDate(entry.lesson.date) : 'Unknown'}
                  </td>
                  <td>
                    {entry.lesson?.topic || "No topic"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => navigate(`/lesson?date=${entry.lesson?.date}&group=${entry.lesson?.group?.id}`)}
                        className="btn-icon"
                        data-tooltip="Edit lesson"
                      >
                        ✎
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1 || loading}
            className="btn-secondary"
          >
            Previous
          </button>
          
          <div className="pagination-info">
            <span>
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
          </div>
          
          <button 
            onClick={() => handlePageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === pagination.totalPages || loading}
            className="btn-secondary"
          >
            Next
          </button>
        </div>
      )}

      {loading && entries.length > 0 && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
};

export default Vocabulary; 